import { Box, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import useFieldCustomize from "components/playbook/hooks/useFieldCustomize";
import React, { FC, useEffect, useRef } from "react";
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormUnregister,
  useFormState,
} from "react-hook-form";
import ConditionRuleEditor from "./ConditionRuleEditor";

const SharedFieldForm: FC<{
  formHeader: string;
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  unregister: UseFormUnregister<any>;
  specComponent: React.ReactNode;
}> = ({ formHeader, fieldId, control, setValue, unregister, specComponent }) => {
  const { getFieldState, setFieldStateList, currentFieldId } = useFieldCustomize();
  const state = getFieldState(fieldId);

  const { errors } = useFormState({ control: control });

  const labelRef = useRef<HTMLInputElement>(null);

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldStateList((items) => {
      return items.map((item) =>
        item.id === state?.id
          ? { ...item, label: { ...item.label, value: event.target.value } }
          : item
      );
    });
    setValue("labelName", event.target.value);
  };

  const handleLabelBlur = () => {
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id ? { ...item, passValidate: !(Object.keys(errors).length > 0) } : item
      )
    );
  };

  const handleLabelValidate = (value: string) => {
    if (value.trim() === "") {
      return "Field name should not be empty string.";
    } else {
      return true;
    }
  };

  const handlePlaceholderChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFieldStateList((items) => {
      return items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              placeholder: { ...item.placeholder, value: event.target.value },
            }
          : item
      );
    });
  };

  const handleRequiredChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFieldStateList((items) => {
      return items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              rules: {
                ...item.rules,
                required: {
                  ...item.rules.required,
                  value: event.target.value,
                },
              },
            }
          : item
      );
    });
  };

  const handleMaxLengthChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFieldStateList((items) => {
      return items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              rules: {
                ...item.rules,
                maxLength: {
                  ...item.rules.maxLength,
                  value: event.target.value,
                },
              },
            }
          : item
      );
    });
    setValue("maxLength", event.target.value);
  };

  useEffect(() => {
    if (labelRef.current) {
      labelRef.current.focus();
    }
  }, [currentFieldId]);

  return (
    <>
      <div
        style={{
          margin: 8,
          paddingTop: 20,
          border: "1px solid #DEDEDE",
          borderRadius: 4,
          padding: 25,
        }}
      >
        <Typography
          variant="h6"
          align="center"
        >
          {formHeader}
        </Typography>
        <Box>
          <p
            style={{
              fontSize: 12,
              color: "#535759",
              fontWeight: 500,
              display: "flex",
            }}
          >
            <span style={{ color: "#FF5F58", fontSize: "1rem", marginRight: 4 }}>*</span>
            {`${state?.label.title}`}
          </p>
          <Controller
            name="labelName"
            control={control}
            defaultValue={state?.label.value}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth={true}
                variant="outlined"
                size="small"
                autoComplete="off"
                placeholder={state?.label.placeholder}
                style={{ background: "#fff" }}
                value={state?.label.value}
                onChange={handleLabelChange}
                onBlur={handleLabelBlur}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={labelRef}
              />
            )}
            rules={{
              required: "This field is required.",
              validate: handleLabelValidate,
            }}
          />
        </Box>
        <Box style={{ marginTop: 10 }}>
          <p
            style={{
              fontSize: 12,
              color: "#535759",
              fontWeight: 500,
              display: "flex",
            }}
          >
            {state?.rules.required?.title}
          </p>
          <RadioGroup
            row
            value={state?.rules.required?.value}
            onChange={handleRequiredChange}
          >
            <FormControlLabel
              label="Mandatory"
              control={<Radio />}
              value="y"
            />
            <FormControlLabel
              label="Optional"
              control={<Radio />}
              value="n"
            />
          </RadioGroup>
        </Box>

        {specComponent}

        {!(
          state?.fieldSubType === "lov" ||
          state?.fieldSubType === "radio" ||
          state?.fieldSubType === "checkbox" ||
          state?.fieldType === "date-picker"
        ) && (
          <Box style={{ marginTop: 10 }}>
            <p
              style={{
                fontSize: 12,
                color: "#535759",
                fontWeight: 500,
                display: "flex",
              }}
            >
              {state?.rules.maxLength?.title}
            </p>

            <Controller
              name="maxLength"
              control={control}
              defaultValue={state?.rules.maxLength.value}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth={true}
                  variant="outlined"
                  size="small"
                  placeholder={state?.rules.maxLength?.placeholder}
                  style={{ background: "#fff" }}
                  value={state?.rules.maxLength?.value}
                  onChange={handleMaxLengthChange}
                  onBlur={handleLabelBlur}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              rules={{
                min: {
                  value: 0,
                  message: "Character limits could not be negative value.",
                },
              }}
            />
          </Box>
        )}

        {!(state?.fieldSubType === "radio" || state?.fieldSubType === "checkbox") && (
          <Box style={{ marginTop: 10 }}>
            <p
              style={{
                fontSize: 12,
                color: "#535759",
                fontWeight: 500,
                display: "flex",
              }}
            >
              {state?.placeholder.title}
            </p>
            <TextField
              fullWidth={true}
              variant="outlined"
              size="small"
              placeholder={state?.placeholder.placeholder}
              style={{ background: "#fff" }}
              value={state?.placeholder.value}
              onChange={handlePlaceholderChange}
            />
          </Box>
        )}
      </div>
      <ConditionRuleEditor
        fieldId={fieldId}
        control={control}
        setValue={setValue}
        unregister={unregister}
      />
    </>
  );
};

export default SharedFieldForm;
